import { useEffect, useState } from "react";
import api from "../../services/api";

interface DeliveryData {
    hasEbook: boolean;
    email: string | null;
    status: "not_sent" | "pending" | "sending" | "sent" | "failed" | "revoked";
    sentCount: number;
    lastSentAt: string | null;
    lastError: string | null;
    files: { id: string; name: string }[];
}

const statusLabel: Record<DeliveryData["status"], { text: string; className: string }> = {
    not_sent: { text: "Não enviado", className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
    pending: { text: "Aguardando envio", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" },
    sending: { text: "Enviando", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" },
    sent: { text: "Enviado", className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
    failed: { text: "Falha no envio", className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" },
    revoked: { text: "Cancelado (reembolso)", className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
};

/**
 * Entrega do e-book por e-mail: mostra para onde foi, quantas vezes, e permite
 * corrigir o e-mail de entrega e reenviar. Trocar o e-mail aqui NÃO altera o
 * cadastro do cliente — vale só para a entrega deste pedido.
 * Some sozinho quando o pedido não tem e-book.
 */
export default function EbookDeliveryCard({ orderId }: { orderId: string }) {
    const [data, setData] = useState<DeliveryData | null>(null);
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    useEffect(() => {
        api.get(`/api/ebook-delivery/orders/${orderId}`)
            .then(res => {
                setData(res.data.data);
                setEmail(res.data.data?.email || "");
            })
            .catch(() => setData(null));
    }, [orderId]);

    if (!data?.hasEbook) return null;

    const handleResend = async () => {
        setSending(true);
        setMessage(null);
        try {
            const res = await api.post(`/api/ebook-delivery/orders/${orderId}/resend`, { email: email.trim() });
            setData(res.data.data);
            setEmail(res.data.data?.email || email);
            setMessage({ type: "success", text: `E-book enviado para ${res.data.data?.email}` });
        } catch (err: any) {
            setMessage({ type: "error", text: err?.response?.data?.message || "Não foi possível reenviar agora" });
        } finally {
            setSending(false);
        }
    };

    const status = statusLabel[data.status] || statusLabel.not_sent;
    const revoked = data.status === "revoked";

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">ENTREGA DO E-BOOK</h3>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}>{status.text}</span>
            </div>

            <ul className="mb-4 space-y-1">
                {data.files.map(f => (
                    <li key={f.id} className="text-sm text-gray-700 dark:text-gray-300">📘 {f.name}</li>
                ))}
            </ul>

            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                {data.sentCount > 0 && data.lastSentAt
                    ? `Enviado ${data.sentCount} ${data.sentCount === 1 ? "vez" : "vezes"} · último envio em ${new Date(data.lastSentAt).toLocaleString("pt-BR")}`
                    : "Ainda não enviado"}
                {data.status === "failed" && data.lastError ? ` · erro: ${data.lastError}` : ""}
            </p>

            <label className="mb-1.5 block text-xs font-medium text-gray-500 uppercase dark:text-gray-400">E-mail de entrega</label>
            <div className="flex flex-col gap-3 sm:flex-row">
                <input
                    type="email"
                    value={email}
                    disabled={revoked || sending}
                    onChange={e => setEmail(e.target.value)}
                    className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-none dark:border-gray-700 dark:text-white/90"
                />
                <button
                    type="button"
                    onClick={handleResend}
                    disabled={revoked || sending || !email.trim()}
                    className="h-11 shrink-0 rounded-lg bg-brand-500 px-5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                    {sending ? "Enviando..." : "Reenviar"}
                </button>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Alterar aqui muda só o destino da entrega deste pedido, não o cadastro do cliente.
            </p>

            {message && (
                <p className={`mt-3 text-sm ${message.type === "success" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                    {message.text}
                </p>
            )}
        </div>
    );
}
